// Billing API Service

import { createApiInstance } from './axios-config';
import API_CONFIG from '../../config/apiConfig';
import {
  BillingAccount,
  Subscription,
  SpecializedProduct,
  SpecializedProductSubscription,
  PaymentMethod,
  Invoice,
  TokenPackage,
  TokenBalance,
  TokenUsage,
  SetupIntentResponse,
  BillingSummary,
  SubscriptionTier,
  BillingFrequency,
  UserProductAssignment
} from '../../types/billing';

const api = createApiInstance(API_CONFIG.BASE_URL);

const BILLING_API_BASE = '/api/v1/billing';

class BillingApi {
  // Account Management
  async getBillingAccount(organizationId: string): Promise<BillingAccount> {
    try {
      const response = await api.get(`${BILLING_API_BASE}/accounts/${organizationId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching billing account:', error);
      throw error;
    }
  }

  async createBillingAccount(organizationId: string, email: string): Promise<BillingAccount> {
    try {
      const response = await api.post(`${BILLING_API_BASE}/accounts`, {
        organizationId,
        email
      });
      return response.data;
    } catch (error) {
      console.error('Error creating billing account:', error);
      throw error;
    }
  }

  // Subscription Management
  async getSubscription(organizationId: string): Promise<Subscription> {
    try {
      const response = await api.get(`${BILLING_API_BASE}/subscriptions/${organizationId}`);
      // The backend returns { success: true, data: subscription }, so we need to extract data
      return response.data.data || response.data;
    } catch (error) {
      console.error('Error fetching subscription:', error);
      throw error;
    }
  }

  async createSubscription(
    organizationId: string,
    tier: SubscriptionTier,
    billingFrequency: BillingFrequency,
    seatCount: number,
    paymentMethodId: string,
    promoCode?: string,
    selectedToolkit?: string
  ): Promise<Subscription> {
    try {
      const response = await api.post(`${BILLING_API_BASE}/subscriptions`, {
        organizationId,
        tier,
        billingFrequency,
        seatCount,
        paymentMethodId,
        promoCode,
        selectedToolkit
      });
      return response.data;
    } catch (error) {
      console.error('Error creating subscription:', error);
      throw error;
    }
  }

  async updateSubscription(
    subscriptionId: string,
    updates: {
      tier?: SubscriptionTier;
      billingFrequency?: BillingFrequency;
      seatCount?: number;
      selectedToolkit?: string;
    }
  ): Promise<Subscription> {
    try {
      console.log('Making PATCH request to:', `${BILLING_API_BASE}/subscriptions/${subscriptionId}`);
      console.log('Request body:', updates);
      const response = await api.patch(`${BILLING_API_BASE}/subscriptions/${subscriptionId}`, updates);
      console.log('PATCH response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  }

  async cancelSubscription(subscriptionId: string): Promise<void> {
    try {
      await api.delete(`${BILLING_API_BASE}/subscriptions/${subscriptionId}`);
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }

  // Specialized Products
  async getSpecializedProducts(): Promise<SpecializedProduct[]> {
    try {
      const response = await api.get(`${BILLING_API_BASE}/specialized-products`);
      return response.data;
    } catch (error) {
      console.error('Error fetching specialized products:', error);
      throw error;
    }
  }

  async getOrganizationProducts(organizationId: string): Promise<SpecializedProductSubscription[]> {
    try {
      const response = await api.get(`${BILLING_API_BASE}/specialized-products/organization/${organizationId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching organization products:', error);
      throw error;
    }
  }

  async addSpecializedProduct(
    organizationId: string,
    productId: string,
    seatCount: number,
    paymentMethodId?: string  // Made optional - Stripe uses default payment method
  ): Promise<SpecializedProductSubscription> {
    try {
      const payload: any = {
        organizationId,
        productId,
        seatCount
      };
      // Only include paymentMethodId if provided
      if (paymentMethodId) {
        payload.paymentMethodId = paymentMethodId;
      }
      const response = await api.post(`${BILLING_API_BASE}/specialized-products/subscribe`, payload);
      return response.data;
    } catch (error) {
      console.error('Error adding specialized product:', error);
      throw error;
    }
  }

  async updateSpecializedProduct(
    subscriptionId: string,
    seatCount: number
  ): Promise<SpecializedProductSubscription> {
    try {
      const response = await api.patch(`${BILLING_API_BASE}/specialized-products/subscription/${subscriptionId}`, {
        seatCount
      });
      return response.data;
    } catch (error) {
      console.error('Error updating specialized product:', error);
      throw error;
    }
  }

  async cancelSpecializedProduct(subscriptionId: string): Promise<void> {
    try {
      await api.delete(`${BILLING_API_BASE}/specialized-products/subscription/${subscriptionId}`);
    } catch (error) {
      console.error('Error canceling specialized product:', error);
      throw error;
    }
  }

  // User Assignment
  async assignUsersToProduct(
    subscriptionId: string,
    userIds: string[]
  ): Promise<UserProductAssignment[]> {
    try {
      const response = await api.post(`${BILLING_API_BASE}/specialized-products/subscription/${subscriptionId}/assign`, {
        userIds
      });
      return response.data;
    } catch (error) {
      console.error('Error assigning users to product:', error);
      throw error;
    }
  }

  async removeUsersFromProduct(
    subscriptionId: string,
    userIds: string[]
  ): Promise<void> {
    try {
      await api.post(`${BILLING_API_BASE}/specialized-products/subscription/${subscriptionId}/unassign`, {
        userIds
      });
    } catch (error) {
      console.error('Error removing users from product:', error);
      throw error;
    }
  }

  // Payment Methods
  async getPaymentMethods(organizationId: string): Promise<PaymentMethod[]> {
    try {
      const response = await api.get(`${BILLING_API_BASE}/payment-methods/organization/${organizationId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      throw error;
    }
  }

  async createSetupIntent(organizationId: string): Promise<SetupIntentResponse> {
    try {
      const response = await api.post(`${BILLING_API_BASE}/payment-methods/setup-intent`, {
        organizationId
      });
      return response.data;
    } catch (error) {
      console.error('Error creating setup intent:', error);
      throw error;
    }
  }

  async addPaymentMethod(
    organizationId: string,
    paymentMethodId: string,
    setDefault: boolean = false
  ): Promise<PaymentMethod> {
    try {
      const response = await api.post(`${BILLING_API_BASE}/payment-methods`, {
        organizationId,
        paymentMethodId,
        setDefault
      });
      return response.data;
    } catch (error) {
      console.error('Error adding payment method:', error);
      throw error;
    }
  }

  async setDefaultPaymentMethod(paymentMethodId: string): Promise<void> {
    try {
      await api.patch(`${BILLING_API_BASE}/payment-methods/${paymentMethodId}/set-default`);
    } catch (error) {
      console.error('Error setting default payment method:', error);
      throw error;
    }
  }

  async deletePaymentMethod(paymentMethodId: string): Promise<void> {
    try {
      await api.delete(`${BILLING_API_BASE}/payment-methods/${paymentMethodId}`);
    } catch (error) {
      console.error('Error deleting payment method:', error);
      throw error;
    }
  }

  // Invoices
  async getInvoices(organizationId: string): Promise<Invoice[]> {
    try {
      const response = await api.get(`${BILLING_API_BASE}/invoices/organization/${organizationId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching invoices:', error);
      throw error;
    }
  }

  async getInvoice(invoiceId: string): Promise<Invoice> {
    try {
      const response = await api.get(`${BILLING_API_BASE}/invoices/${invoiceId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching invoice:', error);
      throw error;
    }
  }

  async downloadInvoice(invoiceId: string): Promise<Blob> {
    try {
      const response = await api.get(`${BILLING_API_BASE}/invoices/${invoiceId}/download`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error downloading invoice:', error);
      throw error;
    }
  }

  // Token Management
  async getTokenPackages(): Promise<TokenPackage[]> {
    try {
      const response = await api.get(`${BILLING_API_BASE}/tokens/packages`);
      return response.data;
    } catch (error) {
      console.error('Error fetching token packages:', error);
      throw error;
    }
  }

  async purchaseTokens(
    organizationId: string,
    packageId: string,
    quantity: number,
    paymentMethodId: string
  ): Promise<{ transactionId: string; newBalance: TokenBalance }> {
    try {
      const response = await api.post(`${BILLING_API_BASE}/tokens/purchase`, {
        organizationId,
        packageId,
        quantity,
        paymentMethodId
      });
      return response.data;
    } catch (error) {
      console.error('Error purchasing tokens:', error);
      throw error;
    }
  }

  async getTokenBalance(organizationId: string): Promise<TokenBalance> {
    try {
      const response = await api.get(`${BILLING_API_BASE}/tokens/balance/${organizationId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching token balance:', error);
      throw error;
    }
  }

  async getTokenUsage(organizationId: string, startDate?: Date, endDate?: Date): Promise<TokenUsage> {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate.toISOString());
      if (endDate) params.append('endDate', endDate.toISOString());
      
      const response = await api.get(`${BILLING_API_BASE}/tokens/usage/${organizationId}?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching token usage:', error);
      throw error;
    }
  }

  // Billing Summary
  async getBillingSummary(organizationId: string): Promise<BillingSummary> {
    try {
      const response = await api.get(`${BILLING_API_BASE}/summary/${organizationId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching billing summary:', error);
      throw error;
    }
  }

  // Promo Codes
  async validatePromoCode(
    code: string,
    tier?: SubscriptionTier,
    billingFrequency?: BillingFrequency
  ): Promise<{
    valid: boolean;
    discount?: {
      type: 'percent_off' | 'amount_off';
      value: number;
      currency?: string;
    };
    error?: string;
  }> {
    try {
      const response = await api.post(`${BILLING_API_BASE}/promo-codes/validate`, {
        code,
        tier,
        billingFrequency
      });
      return response.data;
    } catch (error) {
      console.error('Error validating promo code:', error);
      throw error;
    }
  }
}

export const billingApi = new BillingApi();